import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  Box,
  Grid,
  Tabs,
  Tab,
  AppBar,
  Chip,
  Switch,
} from "@mui/material";
import { useFormik, FormikProvider, Form } from "formik";
import * as Yup from "yup";
import { EMAIL_REGEX, URL_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { apiAuth } from "../../services/AllServices";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function GlobalSetting() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [originalSelected, setOriginalSelected] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [status, setStatus] = useState()
  const [language, setLanguage] = useState([]);

  const settingData = async () => {
    try {
      setLoading(true);

      const response = await apiAuth.getSetting();
      setData(response.data.data);

      const allcountry = await apiAuth.getAllCountry();
      const all = allcountry.data.data.result;

      const selected = all.filter((c) => c.status === 1).map((c) => c.code);

      setCountries(all);
      setOriginalSelected(selected);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);


  const fetchLanguageData = () => {
    setLoading(true);
    const body = {
      searchItem: "",
      pageNumber: '',
      pageSize: '',
      status: status,
    };

    apiAuth
      .getAll(body)
      .then((res) => {
        if (res.status === 200) {
          const result = res?.data?.data?.result || [];

          // Prepare formik values dynamically
          const values = {};
          result.forEach((lang, index) => {
            values[`language${index + 1}`] = lang.name;
            values[`status${index + 1}`] = lang.status === 'active';
          });

          formik.setValues((prev) => ({
            ...prev,
            ...values,
          }));

          setLanguage(result);
        }
      })
      .catch(errorHandler)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLanguageData();
  }, [status]);

  const handleStatusChange = (id, newStatus) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${newStatus ? 'enable' : 'disable'} this language?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, update it!',
    }).then((result) => {
      if (result.isConfirmed) {
        apiAuth
          .changeStatus({ id: id, status: newStatus })
          .then((res) => {
            if (res.status === 200) {
              Swal.fire('Updated!', 'Language status has been changed.', 'success');
              fetchLanguageData(); // refresh list
            }
          })
          .catch((err) => {
            errorHandler(err);
          });
      }
    });
  };




  const updateCountries = async (selectedCountries) => {
    try {
      const payload = [];

      // Removed
      originalSelected.forEach((code) => {
        if (!selectedCountries.includes(code)) {
          payload.push({ code, status: 0 });
        }
      });

      // Added
      selectedCountries.forEach((code) => {
        if (!originalSelected.includes(code)) {
          payload.push({ code, status: 1 });
        }
      });

      if (payload.length > 0) {
        const response = await apiAuth.updateCountry({ countries: payload });

        if (response.status === 200) {
          toast.success("Countries updated successfully");
          setOriginalSelected(selectedCountries);
        }
      } else {
        toast.info("No changes to update.");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const initialValues = {
    instaLink: data?.instaLink ?? "",
    fbLink: data?.fbLink ?? "",
    twitterLink: data?.twitterLink ?? "",
    linkedInLink: data?.linkedInLink ?? "",
    contactUsEmail: data?.contactUsEmail ?? "",
    contestCommission: data?.contestCommission ?? "",
    countries: originalSelected,
    description: data?.description || "",
    location: data?.location || "",
    phone: data?.phone || "",
    languages: data?.languages || [],
    referredBonus: data?.referredBonus ?? "",
    referralBonus: data?.referralBonus ?? "",
    teamBudget: data?.teamBudget ?? "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string()
      .trim()
      .required("Instagram Link is required")
      .matches(URL_REGEX, "Instagram link must be in the format: https://<domain>.com/"),
    fbLink: Yup.string()
      .trim()
      .required("Facebook Link is required")
      .matches(URL_REGEX, "Facebook link must be in the format: https://<domain>.com/"),
    twitterLink: Yup.string()
      .trim()
      .required("Twitter Link is required")
      .matches(URL_REGEX, "Twitter link must be in the format: https://<domain>.com/"),
    linkedInLink: Yup.string()
      .trim()
      .required("LinkedIn Link is required")
      .matches(URL_REGEX, "LinkedIn link must be in the format: https://<domain>.com/"),
    contactUsEmail: Yup.string()
      .required("Contact Us Email is required")
      .trim()
      .matches(EMAIL_REGEX, "Please enter a valid email address")
      .max(255),
    contestCommission: Yup.number()
      .typeError("Contest Commission must be a number")
      .required("Contest Commission is required")
      .min(0, "Must be at least 0")
      .max(100, "Cannot be more than 100"),
    description: Yup.string().optional(),
    location: Yup.string().optional(),
    phone: Yup.string()
      .optional()
      .matches(
        /^(\+?\d{1,3}[- ]?)?\d{10}$/,
        "Phone number is not valid"
      ),
    referredBonus: Yup.number()
      .typeError("Referred Bonus must be a number")
      .required("Referred Bonus is required")
      .min(0, "Must be at least 0")
      .max(1000, "Cannot be more than 1000"),
    referralBonus: Yup.number()
      .typeError("Referral Bonus must be a number")
      .required("Referral Bonus is required")
      .min(0, "Must be at least 0")
      .max(1000, "Cannot be more than 1000"),
    // teamBudget: Yup.number()
    //   .typeError("Team Budget must be a number")
    //   .required("Team Budget is required")
    //   .min(0, "Must be at least 0")
    //   .max(10000, "Cannot be more than 10000"),
    // countries: Yup.array().min(1, "At least one country must be selected"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        console.log("Form submitted with values:", values);

        const payload = {
          instaLink: values.instaLink.trim(),
          fbLink: values.fbLink.trim(),
          twitterLink: values.twitterLink.trim(),
          linkedInLink: values.linkedInLink.trim(),
          contactUsEmail: values.contactUsEmail.trim(),
          contestCommission: values.contestCommission,
          description: values?.description || "",
          location: values?.location || "",
          phone: values?.phone || "",
          languages: values.languages,
          referredBonus: values.referredBonus,
          referralBonus: values.referralBonus,
          teamBudget: values.teamBudget,
        };

        console.log("Sending payload:", payload);

        const response = await apiAuth.updateSetting(payload);
        console.log("API response:", response);

        if (response.status === 200) {
          toast.success(response?.data?.message || "Settings updated successfully!");
          // Refresh the data after successful update
          await settingData();
        } else {
          toast.error("Failed to update settings");
        }

        setSubmitting(false);
      } catch (err) {
        console.error("Update error:", err);
        errorHandler(err);
        setSubmitting(false);
      }
    }
  });

  const { values, touched, errors, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

  const handleCountryChange = (event) => {
    const {
      target: { value },
    } = event;
    const selectedCountries = typeof value === "string" ? value.split(",") : value;
    setFieldValue("countries", selectedCountries);
  };

  const handleTabChange = (event, newTabIndex) => {
    setTabIndex(newTabIndex);
  };

  return (
    <Card>
      <CardContent>
        <FormikProvider value={formik}>
          <Typography variant="h4" gutterBottom>
            Global Settings
          </Typography>

          <br />
          <AppBar position="static">
            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label="Global Settings" />
              <Tab label="Countries" />
              <Tab label="Contact Us" />
              <Tab label="Language" />
              <Tab label="Referral Bonus Settings" />
            </Tabs>
          </AppBar>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {tabIndex === 0 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>Social Media</Typography>
                      <br />
                      <Grid container spacing={2}>
                        {[
                          { name: "fbLink", label: "Facebook Link *" },
                          { name: "twitterLink", label: "Twitter Link *" },
                          { name: "instaLink", label: "Instagram Link *" },
                          { name: "linkedInLink", label: "LinkedIn Link *" },
                        ].map((field) => (
                          <Grid item xs={12} sm={6} key={field.name}>
                            <TextField
                              fullWidth
                              name={field.name}
                              label={field.label}
                              {...getFieldProps(field.name)}
                              error={Boolean(touched[field.name] && errors[field.name])}
                              helperText={touched[field.name] && errors[field.name]}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="h6" gutterBottom>Contact & Commission</Typography>
                      <br />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="contactUsEmail"
                            label="Contact Us Email *"
                            {...getFieldProps("contactUsEmail")}
                            error={Boolean(touched.contactUsEmail && errors.contactUsEmail)}
                            helperText={touched.contactUsEmail && errors.contactUsEmail}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="contestCommission"
                            label="Contest Commission (%) *"
                            {...getFieldProps("contestCommission")}
                            error={Boolean(touched.contestCommission && errors.contestCommission)}
                            helperText={touched.contestCommission && errors.contestCommission}
                            inputProps={{ inputMode: "decimal" }}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d{0,3}$/.test(value)) {
                                formik.setFieldValue("contestCommission", value);
                              }
                            }}
                            onBlur={() => {
                              const value = parseFloat(formik.values.contestCommission);
                              if (!isNaN(value) && value > 100) {
                                formik.setFieldError("contestCommission", "Must be less than or equal to 100");
                              }
                            }}
                          />
                        </Grid>
                        {/* <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="teamBudget"
                            label="Team Budget Amount *"
                            {...getFieldProps("teamBudget")}
                            error={Boolean(touched.teamBudget && errors.teamBudget)}
                            helperText={touched.teamBudget && errors.teamBudget}
                            inputProps={{ inputMode: "decimal" }}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d{0,5}$/.test(value)) {
                                formik.setFieldValue("teamBudget", value);
                              }
                            }}
                            onBlur={() => {
                            }}
                          />
                        </Grid> */}
                      </Grid>
                      {/* <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Team Budget:</strong> Maximum budget allocated for team management and operations
                        </Typography>
                      </Box> */}
                    </Box>
                  </>
                )}

                {tabIndex === 1 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>Available Countries</Typography>
                      <br />
                      <FormControl fullWidth>
                        <Select
                          multiple
                          value={values.countries}
                          onChange={handleCountryChange}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((code) => {
                                const country = countries.find((c) => c.code === code);
                                return (
                                  <Chip
                                    key={code}
                                    label={country ? country.name : code}
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      const newSelection = selected.filter((item) => item !== code);
                                      setFieldValue("countries", newSelection);
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  />
                                );
                              })}
                            </Box>
                          )}

                          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              <Checkbox checked={values.countries.includes(country.code)} />
                              <ListItemText primary={country.name} />
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.countries && errors.countries && (
                          <Typography color="error" variant="body2" mt={1}>
                            {errors.countries}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => updateCountries(values.countries)}
                      >
                        Update Countries
                      </Button>
                    </Box>
                  </>
                )}

                {tabIndex === 2 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>Contact Us</Typography>
                      <br />
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="contactUsEmail"
                            label="Contact Email"
                            {...formik.getFieldProps("contactUsEmail")}
                            fullWidth
                            error={Boolean(
                              touched.contactUsEmail && errors.contactUsEmail
                            )}
                            helperText={
                              touched.contactUsEmail && errors.contactUsEmail
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="phone"
                            label="Phone Number"
                            {...formik.getFieldProps("phone")}
                            fullWidth
                            error={Boolean(touched.phone && errors.phone)}
                            helperText={touched.phone && errors.phone}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="location"
                            label="Location"
                            {...formik.getFieldProps("location")}
                            fullWidth
                            error={Boolean(touched.location && errors.location)}
                            helperText={touched.location && errors.location}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="description"
                            label="Description"
                            {...formik.getFieldProps("description")}
                            fullWidth
                            multiline
                            rows={4}
                            error={Boolean(touched.description && errors.description)}
                            helperText={touched.description && errors.description}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                  </>
                )}

                {tabIndex === 3 && (
                  <Box>
                    <br />
                    <Typography variant="h6" gutterBottom>Languages</Typography>
                    <br />

                    {language.map((lang, index) => (
                      <Grid container spacing={2} alignItems="center" key={lang._id || index} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name={`language${index}`}
                            label={`Language ${index + 1}`}
                            value={lang.name}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Switch
                              checked={lang.status}
                              onChange={() => handleStatusChange(lang._id, !lang.status)}
                              color="primary"
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                )}

                {tabIndex === 4 && (
                  <>
                    <Box>
                      <br />
                      <Typography variant="h6" gutterBottom>Referral Bonus Settings</Typography>
                      <br />
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="referredBonus"
                            label="Referred Bonus Amount *"
                            {...getFieldProps("referredBonus")}
                            error={Boolean(touched.referredBonus && errors.referredBonus)}
                            helperText={touched.referredBonus && errors.referredBonus}
                            inputProps={{ inputMode: "decimal" }}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d{0,4}$/.test(value)) {
                                formik.setFieldValue("referredBonus", value);
                              }
                            }}
                            onBlur={() => {
                             
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="referralBonus"
                            label="Referral Bonus Amount *"
                            {...getFieldProps("referralBonus")}
                            error={Boolean(touched.referralBonus && errors.referralBonus)}
                            helperText={touched.referralBonus && errors.referralBonus}
                            inputProps={{ inputMode: "decimal" }}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d{0,4}$/.test(value)) {
                                formik.setFieldValue("referralBonus", value);
                              }
                            }}
                            onBlur={() => {
                              // No automatic decimal formatting
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Referred Bonus:</strong> Amount given to the person who gets referred
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Referral Bonus:</strong> Amount given to the person who refers someone
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {(tabIndex === 0 || tabIndex === 1 || tabIndex === 2 || tabIndex === 3 || tabIndex === 4) && (
                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="flex-end" pt={2}>
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      {isSubmitting ? "Updating..." : "Update Settings"}
                    </Button>
                    <Button variant="text" onClick={() => navigate("/dashboard")}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Stack>
            </Form>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
